import { townOf, type InquiryAddress } from '@ghp/domain'

/**
 * Le lien d'itinéraire vers une adresse de la tournée (#319) — une URI `geo:` (RFC 5870), que
 * le système remet à **l'application de cartes de la tablette**, celle que l'agent a choisie.
 *
 * ⚠️ **Pas de carte dans l'app, et pas d'URL d'un fournisseur.** GHP DIGIT ouvrait Google Maps
 * dans une vue intégrée : l'adresse d'une propriété privée partait chez Google à chaque
 * consultation, sans que l'agent l'ait décidé. Ici rien ne part tant qu'il n'a pas appuyé, et
 * c'est l'application qu'il a installée qui reçoit l'adresse — un cadrage RGPD encore ouvert ne
 * se tranche pas en codant un fournisseur en dur. `geo:0,0?q=` est la forme « chercher cette
 * adresse » : les coordonnées nulles disent que la position est dans la requête.
 *
 * ⚠️ **Ce que la tablette en fait dépend de son système** : Android ouvre l'application de cartes
 * par défaut ; un Windows sans application qui déclare `geo:` propose d'en chercher une. Le lien
 * ne promet donc que de passer la main.
 *
 * L'adresse est **la postale** — numéro et voie, code postal, bourg (`townOf`) : c'est ce
 * qu'une application de cartes sait chercher. La commune nouvelle du référentiel enverrait à son
 * chef-lieu une maison qui est dans une commune déléguée. `null` quand il n'y a rien à chercher.
 */
export function routeLinkOf(address: InquiryAddress): string | null {
  const locality = [address.postalCode, townOf(address)]
    .filter((part) => part !== null && part.trim() !== '')
    .join(' ')
  const query = [address.street?.trim() ?? '', locality].filter((part) => part !== '').join(', ')

  return query === '' ? null : `geo:0,0?q=${encodeURIComponent(query)}`
}
